import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import {
  ArriveResponse,
  CallNextResponse,
  CheckInResult,
  CountersResponse,
  MoveAction,
  MoveResponse,
  PointQueue,
  QUEUE_ERROR_CODES,
  QueueErrorCode,
  QueueListResponse,
  SessionResponse,
  TicketStatus,
  TicketView,
} from '../models/queue.model';
import { LambdaError, SupabaseService } from './base/supabase.service';

const PATHS = {
  arrive: 'queue-arrive',
  counters: 'queue-counters',
  list: 'queue-list',
  session: 'counter-session',
  callNext: 'queue-call-next',
  move: 'queue-ticket-move',
} as const;

const SESSION_STORAGE_KEY = 'deskCounterSession';

export const SIGN_OUT_CLOSE_TIMEOUT_MS = 3000;

interface RememberedSession {
  userId: string;
  counterId: string;
}

export function queueErrorCode(error: unknown): QueueErrorCode | null {
  const details = (error as LambdaError | null | undefined)?.body?.details;
  const code = details && typeof details === 'object' ? details['code'] : undefined;
  return typeof code === 'string' && (QUEUE_ERROR_CODES as readonly string[]).includes(code)
    ? (code as QueueErrorCode)
    : null;
}

/** A senha que o 409 ALREADY_CHECKED_IN devolve em `details.ticket` (sem o nome do titular). */
export function queueErrorTicket(error: unknown): TicketView | null {
  const ticket = (error as LambdaError | null | undefined)?.body?.details?.['ticket'];
  return ticket && typeof ticket === 'object' ? (ticket as TicketView) : null;
}

@Injectable({
  providedIn: 'root',
})
export class QueueService {
  constructor(private sbs: SupabaseService) {}

  /** Emite a senha da reserva de hoje na fila escolhida (check-in da triagem) e manda o SMS. */
  arrive(appointmentId: string, queueId: string): Promise<ArriveResponse> {
    return this.sbs.callLambda(PATHS.arrive, 'POST', { appointment_id: appointmentId, queue_id: queueId });
  }

  /**
   * `arrive` em que "já fez check-in" não é erro: um segundo clique da triagem mostra a mesma
   * senha em vez de falhar. Qualquer outro erro sobe como veio.
   */
  async checkIn(appointmentId: string, queueId: string): Promise<CheckInResult> {
    try {
      const { ticket, sms } = await this.arrive(appointmentId, queueId);
      return { ticket, sms, already: false };
    } catch (error) {
      const existing = queueErrorCode(error) === 'ALREADY_CHECKED_IN' ? queueErrorTicket(error) : null;
      if (existing) return { ticket: existing, sms: null, already: true };
      throw error;
    }
  }

  /**
   * Filas ativas do ponto, na ordem de prioridade. Leitura direta: o AGENT com vínculo no ponto
   * lê pela policy AGENT_select_cp_service_queue e o ADMINISTRATOR pela dele. Não usa o
   * service-catalog-list, que é só ADMINISTRATOR. A tabela não está nos tipos gerados.
   */
  async listPointQueues(servicePointId: string): Promise<PointQueue[]> {
    const db = this.sbs.supabase as unknown as SupabaseClient;
    const { data, error } = await db
      .from('cp_service_queue')
      .select('id, name, color, ticket_letter, priority_order')
      .eq('service_point_id', servicePointId)
      .eq('active', true)
      .order('priority_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as PointQueue[];
  }

  counters(servicePointId: string): Promise<CountersResponse> {
    return this.sbs.callLambda(PATHS.counters, 'POST', { service_point_id: servicePointId });
  }

  list(servicePointId: string, statuses?: readonly TicketStatus[]): Promise<QueueListResponse> {
    const body: Record<string, unknown> = { service_point_id: servicePointId };
    if (statuses) body['statuses'] = statuses;
    return this.sbs.callLambda(PATHS.list, 'POST', body);
  }

  openSession(counterId: string): Promise<SessionResponse> {
    return this.sbs.callLambda(PATHS.session, 'POST', { counter_id: counterId, action: 'OPEN' });
  }

  closeSession(counterId: string): Promise<SessionResponse> {
    return this.sbs.callLambda(PATHS.session, 'POST', { counter_id: counterId, action: 'CLOSE' });
  }

  callNext(counterId: string): Promise<CallNextResponse> {
    return this.sbs.callLambda(PATHS.callNext, 'POST', { counter_id: counterId });
  }

  move(ticketId: string, action: MoveAction): Promise<MoveResponse> {
    return this.sbs.callLambda(PATHS.move, 'POST', { ticket_id: ticketId, action });
  }

  rememberSession(userId: string, counterId: string): void {
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ userId, counterId }));
    } catch (error) {
      console.warn('[QUEUE] Could not remember the counter session', error);
    }
  }

  forgetSession(): void {
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.warn('[QUEUE] Could not forget the counter session', error);
    }
  }

  async closeSessionOnSignOut(userId: string | null): Promise<void> {
    const remembered = this.readRememberedSession();
    this.forgetSession();
    if (!userId || !remembered || remembered.userId !== userId) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, SIGN_OUT_CLOSE_TIMEOUT_MS);
    });
    try {
      await Promise.race([this.closeSession(remembered.counterId), timeout]);
    } catch (error) {
      console.warn('[QUEUE] Counter session was not closed on sign out', error);
    } finally {
      clearTimeout(timer);
    }
  }

  private readRememberedSession(): RememberedSession | null {
    try {
      const raw = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return typeof parsed?.userId === 'string' && typeof parsed?.counterId === 'string' ? parsed : null;
    } catch (error) {
      return null;
    }
  }
}
